import { Container, Overline, SectionHeading, Reveal } from "@/components/primitives";
import { IMAGES } from "@/lib/images";

// 1 — The problem VillaFlow solves
export function ProblemSection() {
  return (
    <section className="py-28 md:py-40" data-testid="problem-section">
      <Container>
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          <div className="lg:col-span-6">
            <Reveal>
              <Overline>Why VillaFlow</Overline>
              <SectionHeading className="mt-8">
                Luxury villas have the space. Hotels have the system.
              </SectionHeading>
            </Reveal>
          </div>
          <div className="lg:col-span-5 lg:col-start-8">
            <Reveal delay={0.15}>
              <p className="text-ivory/70 text-base md:text-lg font-light leading-relaxed">
                Exceptional villas offer what no hotel can — location, privacy, architecture and space.
                Yet a private villa rarely comes with the organisation and operating standards of a
                five-star hotel. Too often the experience depends on a single owner or manager.
              </p>
              <div className="gold-rule my-10 max-w-[120px]" />
              <p className="font-serif text-2xl md:text-3xl text-ivory/90 leading-snug">
                We bring the system of luxury hospitality
                <span className="text-gold"> to the world of private villas.</span>
              </p>
            </Reveal>
          </div>
        </div>
      </Container>
    </section>
  );
}

// 2 — The best of both worlds
const HOTEL = ["Professional service", "Housekeeping", "Concierge", "Personalized welcome", "Food & beverage", "Guest support", "Consistent standards", "Quality control"];
const VILLA = ["Absolute privacy", "More space", "Private pool", "Private outdoor areas", "Authentic architecture", "Exclusive location", "Freedom", "Family & friends"];

function Column({ label, items, align = "left" }) {
  return (
    <div className={align === "right" ? "lg:text-right" : ""}>
      <span className="block text-[11px] tracking-[0.3em] uppercase text-ivory/45 mb-6">{label}</span>
      <ul className="space-y-3">
        {items.map((i) => (
          <li key={i} className="text-ivory/75 font-light text-sm md:text-base">{i}</li>
        ))}
      </ul>
    </div>
  );
}

export function BestOfBothWorlds() {
  return (
    <section className="py-28 md:py-40 bg-navy-800 border-y border-ivory/10" data-testid="both-worlds">
      <Container>
        <div className="max-w-3xl mx-auto text-center mb-20">
          <Reveal>
            <Overline>The Best of Both Worlds</Overline>
            <SectionHeading className="mt-8">The best of both worlds.</SectionHeading>
            <p className="mt-6 text-ivory/65 text-lg font-light">
              The service of a 5-star hotel. The freedom of a private villa.
            </p>
          </Reveal>
        </div>

        <div className="grid lg:grid-cols-3 gap-10 lg:gap-8 items-center">
          <Reveal><Column label="5-Star Hotel" items={HOTEL} align="right" /></Reveal>
          <Reveal delay={0.12}>
            <div className="border border-gold/30 py-12 px-6 text-center bg-navy-900">
              <span className="font-serif text-3xl md:text-4xl text-gold">VillaFlow</span>
              <p className="mt-4 text-ivory/70 font-light text-sm leading-relaxed">
                5-star hospitality standards + private villa living.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.2}><Column label="Private Villa" items={VILLA} /></Reveal>
        </div>

        <Reveal delay={0.1}>
          <p className="mt-20 text-center font-serif text-2xl md:text-4xl text-ivory font-light max-w-4xl mx-auto leading-snug tracking-tight">
            You no longer have to choose between the service of a luxury hotel and the
            <span className="text-gold italic"> privacy of a private villa.</span>
          </p>
        </Reveal>
      </Container>
    </section>
  );
}

// 3 — The VillaFlow 5-Star Standard (8 areas)
const AREAS = [
  { n: "01", t: "Property", d: "Quality, maintenance, design, comfort, amenities and condition of the villa." },
  { n: "02", t: "Arrival & Welcome", d: "Reception, check-in, welcome experience, arrival assistance and first impression." },
  { n: "03", t: "Housekeeping", d: "Cleanliness, order, service frequency, linens, amenities and housekeeping standards." },
  { n: "04", t: "Service", d: "Response times, in-stay assistance, problem solving and personalised attention." },
  { n: "05", t: "Food & Beverage", d: "Private chef, breakfast, aperitifs, wine experiences and gastronomic service." },
  { n: "06", t: "Concierge", d: "Experiences, restaurants, transfers, excursions, yachts, beach clubs and planning." },
  { n: "07", t: "Guest Experience", d: "Personalisation, attention to detail, communication and anticipating every need." },
  { n: "08", t: "Quality Control", d: "Continuous monitoring of standards and structured guest feedback." },
];

export function FiveStarStandard() {
  return (
    <section className="py-28 md:py-40" data-testid="five-star-standard">
      <Container>
        <div className="max-w-3xl mb-20">
          <Reveal>
            <Overline>The Framework</Overline>
            <SectionHeading className="mt-8">
              The VillaFlow 5-Star Standard<span className="text-gold align-super text-xl">™</span>
            </SectionHeading>
            <p className="mt-8 text-ivory/65 text-lg font-light leading-relaxed">
              A proprietary system that measures and manages the guest experience — inspired by the
              standards of luxury hotellerie, applied to private villas.
            </p>
          </Reveal>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-ivory/10 border border-ivory/10">
          {AREAS.map((a, i) => (
            <Reveal key={a.n} delay={(i % 4) * 0.06}>
              <div className="bg-navy-900 p-8 h-full group hover:bg-navy-800 transition-colors duration-700">
                <span className="font-serif text-gold/40 text-3xl group-hover:text-gold/70 transition-colors duration-700">{a.n}</span>
                <h3 className="font-serif text-2xl text-ivory mt-4">{a.t}</h3>
                <p className="mt-3 text-ivory/50 text-sm font-light leading-relaxed">{a.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Container>
    </section>
  );
}

// 4 — The guest journey
const JOURNEY = [
  { t: "Before Arrival", items: ["Personalized communication", "Concierge planning", "Transfer & experiences", "Villa preparation"] },
  { t: "Arrival", items: ["Welcome", "Check-in", "Amenities", "Personalized touches"] },
  { t: "During the Stay", items: ["Housekeeping", "Concierge", "Private chef", "Experiences", "Guest support"] },
  { t: "Before Departure", items: ["Assistance", "Final arrangements", "Feedback"] },
  { t: "After the Stay", items: ["Guest relationship", "Feedback", "Return experience"] },
];

export function GuestJourney() {
  return (
    <section className="relative py-28 md:py-40 overflow-hidden" data-testid="guest-journey">
      <img src={IMAGES.terraceSunset} alt="" className="absolute inset-0 w-full h-full object-cover opacity-15" />
      <div className="absolute inset-0 bg-navy-900/85" />
      <Container className="relative">
        <div className="max-w-3xl mb-16">
          <Reveal>
            <Overline>The Guest Journey</Overline>
            <SectionHeading className="mt-8">From arrival to departure, every detail matters.</SectionHeading>
          </Reveal>
        </div>
        <div className="grid md:grid-cols-5 gap-px bg-ivory/10 border border-ivory/10">
          {JOURNEY.map((p, i) => (
            <Reveal key={p.t} delay={i * 0.06}>
              <div className="bg-navy-900/70 backdrop-blur-sm p-6 md:p-7 h-full">
                <span className="font-serif text-gold/40 text-3xl">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="font-serif text-xl text-ivory mt-3">{p.t}</h3>
                <ul className="mt-4 space-y-2">
                  {p.items.map((it) => (
                    <li key={it} className="text-ivory/55 text-[13px] font-light flex gap-2"><span className="text-gold">·</span>{it}</li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.1}>
          <p className="mt-16 font-serif text-2xl md:text-3xl text-ivory/90 font-light text-center max-w-3xl mx-auto">
            A 5-star experience is not one service. <span className="text-gold italic">It's every detail working together.</span>
          </p>
        </Reveal>
      </Container>
    </section>
  );
}
